// src/pages/Orders/OrderForm/OrderFormSections/EnhancedExchangeSection.jsx
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "../../../../components/common/Button";
import Badge from "../../../../components/common/Badge";
import Modal from "../../../../components/common/Modal";
import Input from "../../../../components/common/Input";
import {
  calculateExchangeValueThunk,
  clearExchangeCalculation,
} from "../../../../store/thunks/orderThunks";
import {
  FiRefreshCw,
  FiPlus,
  FiTrash2,
  FiEdit3,
  FiDollarSign,
  FiChevronDown,
  FiChevronUp,
  FiAlertCircle,
  FiCheck,
  FiInfo,
  FiRotateCcw,
} from "react-icons/fi";

const ITEM_TYPES = [
  { value: "GOLD", label: "Gold", color: "yellow" },
  { value: "SILVER", label: "Silver", color: "gray" },
  { value: "PLATINUM", label: "Platinum", color: "slate" },
  { value: "DIAMOND", label: "Diamond", color: "blue" },
  { value: "JEWELRY", label: "Jewelry", color: "purple" },
];

const CONDITION_GRADES = [
  {
    value: "EXCELLENT",
    label: "Excellent",
    description: "Like new, minimal wear",
    color: "green",
  },
  {
    value: "GOOD",
    label: "Good",
    description: "Light wear, good condition",
    color: "blue",
  },
  {
    value: "FAIR",
    label: "Fair",
    description: "Visible wear, functional",
    color: "yellow",
  },
  {
    value: "POOR",
    label: "Poor",
    description: "Heavy wear, needs repair",
    color: "red",
  },
];

const METAL_PURITIES = {
  GOLD: ["24K", "22K", "18K", "14K", "10K"],
  SILVER: ["999", "925", "900", "800"],
  PLATINUM: ["PT950", "PT900", "PT850"],
};

const METAL_COLORS = {
  GOLD: ["Yellow", "White", "Rose"],
  SILVER: ["White", "Black"],
  PLATINUM: ["White", "Gray"],
};

const EnhancedExchangeSection = ({
  exchangeItems = [],
  onAddItem,
  onUpdateItem,
  onRemoveItem,
  errors,
  readOnly = false,
  adminInfo,
}) => {
  const dispatch = useDispatch();

  // Get calculation state from Redux
  const { calculation } = useSelector((state) => state.orders.exchangeItems);
  const { selectedBranch } = useSelector((state) => state.rate.ui);
  const {
    appraisal,
    loading: isCalculating,
    error: calculationError,
  } = calculation;

  const [isExpanded, setIsExpanded] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [showManualAdjustment, setShowManualAdjustment] = useState(false);
  const [formData, setFormData] = useState(getInitialFormData());

  function getInitialFormData() {
    return {
      itemType: "GOLD",
      description: "",
      brand: "",
      grossWeight: "",
      netWeight: "",
      stoneWeight: "0",
      metalDetails: {
        purity: "22K",
        color: "Yellow",
        karat: 22,
      },
      conditionGrade: "GOOD",
      conditionNotes: "",
      images: [],
      agreedValue: "",
      adjustmentReason: "",
      fulfillmentLocation: {
        branchCode: selectedBranch,
      },
    };
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  // Auto-calculate when key fields change
  useEffect(() => {
    if (
      formData.netWeight &&
      formData.metalDetails?.purity &&
      formData.itemType &&
      selectedBranch
    ) {
      const timeoutId = setTimeout(() => {
        handleAutoCalculate();
      }, 800); // Debounce 800ms

      return () => clearTimeout(timeoutId);
    }
  }, [
    formData.netWeight,
    formData.metalDetails?.purity,
    formData.itemType,
    formData.conditionGrade,
    formData.stoneWeight,
    selectedBranch,
  ]);

  // Clear calculation when modal closes
  useEffect(() => {
    if (!showAddModal) {
      dispatch(clearExchangeCalculation());
      setShowManualAdjustment(false);
    }
  }, [showAddModal, dispatch]);

  const handleAutoCalculate = async () => {
    // Validate required fields
    if (
      !formData.netWeight ||
      !formData.metalDetails?.purity ||
      !formData.itemType
    ) {
      return;
    }

    if (!selectedBranch) {
      console.warn("Branch code not available for calculation");
      return;
    }

    // Dispatch the thunk
    try {
      await dispatch(
        calculateExchangeValueThunk({
          branchCode: selectedBranch,
          itemType: formData.itemType,
          grossWeight: formData.grossWeight || formData.netWeight,
          netWeight: formData.netWeight,
          stoneWeight: formData.stoneWeight || 0,
          metalDetails: formData.metalDetails,
          conditionGrade: formData.conditionGrade,
        })
      ).unwrap();
    } catch (error) {
      console.error("Calculate error:", error);
      // Error is already in Redux state, will show in UI
    }
  };

  console.log(">>> ", exchangeItems);

  const handleSave = () => {
    // Validate
    if (!formData.itemType) {
      alert("Please select item type");
      return;
    }
    if (!formData.description || formData.description.trim().length < 5) {
      alert("Description is required (min 5 characters)");
      return;
    }
    if (!formData.grossWeight || formData.grossWeight <= 0) {
      alert("Gross weight is required");
      return;
    }
    if (!formData.netWeight || formData.netWeight <= 0) {
      alert("Net weight is required");
      return;
    }
    if (!formData.conditionGrade) {
      alert("Please select condition grade");
      return;
    }

    // Must have appraisal or manual value
    if (!appraisal && !showManualAdjustment) {
      alert("Please wait for value calculation or enable manual adjustment");
      return;
    }

    // If manual adjustment, validate the value
    if (showManualAdjustment) {
      if (!formData.agreedValue || formData.agreedValue <= 0) {
        alert("Please enter a valid adjusted value");
        return;
      }
      if (
        !formData.adjustmentReason ||
        formData.adjustmentReason.trim().length < 5
      ) {
        alert(
          "Please provide a reason for manual adjustment (min 5 characters)"
        );
        return;
      }
    }

    console.log(">>> ", exchangeItems);

    // Build item with backend-calculated values
    const itemToSave = {
      exchangeItemId:
        editingIndex !== null
          ? exchangeItems[editingIndex].exchangeItemId
          : `EXCH-${Date.now()}`,
      itemType: formData.itemType,
      description: formData.description.trim(),
      brand: formData.brand?.trim() || undefined,
      grossWeight: parseFloat(formData.grossWeight),
      netWeight: parseFloat(formData.netWeight),
      stoneWeight: parseFloat(formData.stoneWeight) || 0,
      metalDetails: formData.metalDetails,
      conditionGrade: formData.conditionGrade,
      conditionNotes: formData.conditionNotes?.trim() || undefined,
      images: formData.images || [],

      // Use backend-calculated appraisal
      systemAppraisal: appraisal
        ? {
            grossValue: appraisal.grossValue,
            deductions: appraisal.deductions,
            totalDeductions: appraisal.totalDeductions,
            finalValue: appraisal.finalValue,
            calculatedAt: new Date().toISOString(),
            calculatedBy: adminInfo?.adminCode || "SYSTEM",
          }
        : undefined,

      // Check if manually adjusted
      manualAdjustment:
        showManualAdjustment && formData.agreedValue !== appraisal?.finalValue
          ? {
              adjustedValue: parseFloat(formData.agreedValue),
              adjustmentReason: formData.adjustmentReason.trim(),
              adjustedBy: adminInfo?.adminCode || "ADMIN",
              adjustedAt: new Date().toISOString(),
            }
          : undefined,

      // Use adjusted value if provided, otherwise system value
      agreedValue: showManualAdjustment
        ? parseFloat(formData.agreedValue)
        : appraisal?.finalValue || 0,

      status: "APPRAISED",
      customerAccepted: false,
      addedBy: adminInfo?.adminCode || "ADMIN",
      addedAt:
        editingIndex !== null
          ? exchangeItems[editingIndex].addedAt
          : new Date().toISOString(),
    };

    if (editingIndex !== null) {
      onUpdateItem(editingIndex, itemToSave);
    } else {
      onAddItem(itemToSave);
    }

    setShowAddModal(false);
    setFormData(getInitialFormData());
    dispatch(clearExchangeCalculation());
    setShowManualAdjustment(false);
  };

  // Calculate total based on accepted items and their agreed values
  const totalExchangeValue = exchangeItems.reduce((sum, item) => {
    if (item.customerAccepted || item.status === "ACCEPTED") {
      return sum + (item.agreedValue || item.systemAppraisal?.finalValue || 0);
    }
    return sum;
  }, 0);

  const acceptedItems = exchangeItems.filter(
    (item) => item.customerAccepted || item.status === "ACCEPTED"
  );
  const pendingItems = exchangeItems.filter(
    (item) => !item.customerAccepted && item.status !== "ACCEPTED"
  );

  const handleOpenAddModal = () => {
    setFormData(getInitialFormData());
    setEditingIndex(null);
    setShowManualAdjustment(false);
    setShowAddModal(true);
  };

  const handleOpenEditModal = (index) => {
    const item = exchangeItems[index];
    setFormData({
      itemType: item.itemType,
      description: item.description,
      brand: item.brand || "",
      grossWeight: item.grossWeight,
      netWeight: item.netWeight,
      stoneWeight: item.stoneWeight || 0,
      metalDetails: item.metalDetails || {
        purity: "22K",
        color: "Yellow",
        karat: 22,
      },
      conditionGrade: item.conditionGrade,
      conditionNotes: item.conditionNotes || "",
      images: item.images || [],
      agreedValue: item.agreedValue || "",
      adjustmentReason: item.manualAdjustment?.adjustmentReason || "",
    });
    setEditingIndex(index);
    setShowManualAdjustment(!!item.manualAdjustment);
    setShowAddModal(true);
  };

  const handleChange = (field, value) => {
    if (field.includes(".")) {
      const [parent, child] = field.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: value }));
    }
  };

  // Auto-calculate net weight
  const calculateNetWeight = () => {
    const gross = parseFloat(formData.grossWeight) || 0;
    const stone = parseFloat(formData.stoneWeight) || 0;
    const net = gross - stone;
    if (net > 0 && net !== parseFloat(formData.netWeight)) {
      handleChange("netWeight", net.toFixed(3));
    }
  };

  const handleAcceptItem = (index) => {
    const updatedItem = {
      ...exchangeItems[index],
      status: "ACCEPTED",
      customerAccepted: true,
      agreedValue:
        exchangeItems[index].agreedValue ||
        exchangeItems[index].systemAppraisal?.finalValue ||
        0,
      agreedAt: new Date().toISOString(),
    };
    onUpdateItem(index, updatedItem);
  };

  const handleUnacceptItem = (index) => {
    if (
      !window.confirm(
        "Reset this accepted item? It will move back to pending status."
      )
    ) {
      return;
    }
    const updatedItem = {
      ...exchangeItems[index],
      status: "APPRAISED",
      customerAccepted: false,
    };
    onUpdateItem(index, updatedItem);
  };

  const getStatusBadge = (item) => {
    if (item.customerAccepted || item.status === "ACCEPTED") {
      return { variant: "green", label: "Accepted" };
    }

    const badges = {
      PENDING: { variant: "yellow", label: "Pending" },
      APPRAISED: { variant: "blue", label: "Appraised" },
      NEGOTIATED: { variant: "purple", label: "Negotiated" },
      REJECTED: { variant: "red", label: "Rejected" },
    };
    return badges[item.status] || badges.PENDING;
  };

  const getConditionColor = (grade) => {
    const colors = {
      EXCELLENT: "green",
      GOOD: "blue",
      FAIR: "yellow",
      POOR: "red",
    };
    return colors[grade] || "gray";
  };

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Header */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <FiRefreshCw className="w-5 h-5 text-primary" />
          <div className="text-left">
            <h3 className="text-lg font-semibold text-gray-900">
              Exchange Items ({exchangeItems.length})
            </h3>
            {exchangeItems.length > 0 && (
              <p className="text-sm text-gray-500">
                {acceptedItems.length} accepted • {pendingItems.length} pending
              </p>
            )}
          </div>
          {totalExchangeValue > 0 && (
            <Badge variant="green" size="sm">
              {formatCurrency(totalExchangeValue)}
            </Badge>
          )}
        </div>
        {isExpanded ? (
          <FiChevronUp className="w-5 h-5 text-gray-400" />
        ) : (
          <FiChevronDown className="w-5 h-5 text-gray-400" />
        )}
      </button>

      {/* Content */}
      {isExpanded && (
        <div className="px-6 pb-6 border-t border-gray-200">
          <div className="mt-4 space-y-4">
            {exchangeItems.length > 0 ? (
              <>
                {/* Exchange Items Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {exchangeItems.map((item, index) => (
                    <ExchangeItemCard
                      key={item.exchangeItemId || index}
                      item={item}
                      index={index}
                      onEdit={() => handleOpenEditModal(index)}
                      onRemove={() => onRemoveItem(index)}
                      onAccept={() => handleAcceptItem(index)}
                      onUnaccept={() => handleUnacceptItem(index)}
                      formatCurrency={formatCurrency}
                      getStatusBadge={getStatusBadge}
                      getConditionColor={getConditionColor}
                      readOnly={readOnly}
                    />
                  ))}
                </div>

                {/* Add Another Button */}
                {!readOnly && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleOpenAddModal}
                    className="w-full"
                  >
                    <FiPlus className="w-4 h-4 mr-2" />
                    Add Another Exchange Item
                  </Button>
                )}

                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Total Items */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <FiRefreshCw className="text-blue-600" />
                      <span className="text-sm font-medium text-blue-900">
                        Total Items
                      </span>
                    </div>
                    <p className="text-2xl font-bold text-blue-700">
                      {exchangeItems.length}
                    </p>
                  </div>

                  {/* Accepted */}
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <FiCheck className="text-green-600" />
                      <span className="text-sm font-medium text-green-900">
                        Accepted
                      </span>
                    </div>
                    <p className="text-2xl font-bold text-green-700">
                      {acceptedItems.length}
                    </p>
                  </div>

                  {/* Total Value */}
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <FiDollarSign className="text-green-600" />
                      <span className="text-sm font-medium text-green-900">
                        Total Value
                      </span>
                    </div>
                    <p className="text-2xl font-bold text-green-700">
                      {formatCurrency(totalExchangeValue)}
                    </p>
                  </div>
                </div>
              </>
            ) : (
              /* Empty State */
              <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
                <FiRefreshCw className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-lg font-medium text-gray-900 mb-2">
                  No exchange items added yet
                </p>
                <p className="text-gray-500 mb-6">
                  Add items that the customer wants to exchange for credit
                </p>
                {!readOnly && (
                  <Button
                    type="button"
                    variant="primary"
                    onClick={handleOpenAddModal}
                  >
                    <FiPlus className="w-4 h-4 mr-2" />
                    Add Exchange Item
                  </Button>
                )}
              </div>
            )}

            {/* Error Display */}
            {errors?.exchangeItems && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <FiAlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-600">{errors.exchangeItems}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Add/Edit Modal */}
      <Modal
        isOpen={showAddModal}
        closeModal={() => {
          if (!readOnly) {
            setShowAddModal(false);
            setFormData(getInitialFormData());
            dispatch(clearExchangeCalculation());
            setShowManualAdjustment(false);
          }
        }}
        title={
          editingIndex !== null ? "Edit Exchange Item" : "Add Exchange Item"
        }
        maxWidth="3xl"
      >
        <div className="space-y-6">
          {/* Item Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Item Type *
            </label>
            <div className="grid grid-cols-5 gap-3">
              {ITEM_TYPES.map((type) => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => handleChange("itemType", type.value)}
                  className={`p-3 rounded-lg border-2 text-center transition-all ${
                    formData.itemType === type.value
                      ? "border-primary bg-primary-50 ring-2 ring-primary-200"
                      : "border-gray-300 hover:border-gray-400"
                  }`}
                >
                  <p className="font-medium text-sm">{type.label}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Description & Brand */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Input
                label="Description *"
                value={formData.description}
                onChange={(e) => handleChange("description", e.target.value)}
                placeholder="e.g., Old gold chain with pendant"
                required
              />
            </div>

            <Input
              label="Brand (Optional)"
              value={formData.brand}
              onChange={(e) => handleChange("brand", e.target.value)}
              placeholder="e.g., Tanishq, PC Jeweller"
            />
          </div>

          {/* Weight Details */}
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center gap-2 mb-3">
              <FiInfo className="text-blue-600" />
              <h4 className="font-medium text-gray-900">
                Weight Details (in grams)
              </h4>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <Input
                label="Gross Weight *"
                type="number"
                step="0.001"
                value={formData.grossWeight}
                onChange={(e) => handleChange("grossWeight", e.target.value)}
                onBlur={calculateNetWeight}
                placeholder="0.000"
                required
              />

              <Input
                label="Stone Weight"
                type="number"
                step="0.001"
                value={formData.stoneWeight}
                onChange={(e) => handleChange("stoneWeight", e.target.value)}
                onBlur={calculateNetWeight}
                placeholder="0.000"
              />

              <Input
                label="Net Weight *"
                type="number"
                step="0.001"
                value={formData.netWeight}
                onChange={(e) => handleChange("netWeight", e.target.value)}
                placeholder="0.000"
                required
              />
            </div>
            <p className="text-xs text-blue-700 mt-2">
              💡 Net Weight = Gross Weight - Stone Weight
            </p>
          </div>

          {/* Metal Details */}
          {["GOLD", "SILVER", "PLATINUM"].includes(formData.itemType) && (
            <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
              <h4 className="font-medium text-gray-900 mb-3">Metal Details</h4>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Purity *
                  </label>
                  <select
                    value={formData.metalDetails.purity}
                    onChange={(e) =>
                      handleChange("metalDetails.purity", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">Select purity</option>
                    {METAL_PURITIES[formData.itemType]?.map((purity) => (
                      <option key={purity} value={purity}>
                        {purity}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Color
                  </label>
                  <select
                    value={formData.metalDetails.color}
                    onChange={(e) =>
                      handleChange("metalDetails.color", e.target.value)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">Select color</option>
                    {METAL_COLORS[formData.itemType]?.map((color) => (
                      <option key={color} value={color}>
                        {color}
                      </option>
                    ))}
                  </select>
                </div>

                {formData.itemType === "GOLD" && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Karat
                    </label>
                    <input
                      type="number"
                      value={formData.metalDetails.karat}
                      onChange={(e) =>
                        handleChange("metalDetails.karat", e.target.value)
                      }
                      min="10"
                      max="24"
                      placeholder="e.g., 22"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Condition Grade */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Condition Grade *
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {CONDITION_GRADES.map((grade) => (
                <button
                  key={grade.value}
                  type="button"
                  onClick={() => handleChange("conditionGrade", grade.value)}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${
                    formData.conditionGrade === grade.value
                      ? "border-primary bg-primary-50 ring-2 ring-primary-200"
                      : "border-gray-300 hover:border-gray-400"
                  }`}
                >
                  <p className="font-medium text-sm">{grade.label}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {grade.description}
                  </p>
                </button>
              ))}
            </div>
          </div>

          {/* Condition Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Condition Notes (Optional)
            </label>
            <textarea
              value={formData.conditionNotes}
              onChange={(e) => handleChange("conditionNotes", e.target.value)}
              rows={3}
              placeholder="Any scratches, damages, missing parts, etc..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none"
            />
          </div>

          {/* Calculation Display */}
          {!selectedBranch ? (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-center gap-2">
                <FiAlertCircle className="text-yellow-600" />
                <span className="text-sm text-yellow-800">
                  Please select fulfillment location first to calculate exchange
                  value
                </span>
              </div>
            </div>
          ) : isCalculating ? (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent" />
                <span className="text-sm text-blue-700">
                  Calculating value based on current rates...
                </span>
              </div>
            </div>
          ) : calculationError ? (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start gap-2 mb-2">
                <FiAlertCircle className="text-red-600 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-red-700 font-medium mb-1">
                    Calculation Error
                  </p>
                  <p className="text-sm text-red-600">{calculationError}</p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleAutoCalculate}
                className="text-sm text-red-600 hover:text-red-700 underline"
              >
                Try again
              </button>
            </div>
          ) : appraisal ? (
            <div className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <FiCheck className="text-green-600" />
                System Appraisal
              </h4>

              {/* Gross Value */}
              <div className="flex justify-between mb-2">
                <span className="text-sm text-gray-600">Gross Value:</span>
                <span className="font-semibold text-gray-900">
                  {formatCurrency(appraisal.grossValue)}
                </span>
              </div>

              {/* Deductions Breakdown */}
              {appraisal.totalDeductions > 0 && (
                <div className="space-y-1 mb-2 pl-4">
                  {Object.entries(appraisal.deductions).map(([key, value]) => {
                    if (value > 0) {
                      return (
                        <div
                          key={key}
                          className="flex justify-between text-sm text-red-600"
                        >
                          <span className="capitalize">
                            {key.replace(/([A-Z])/g, " $1").trim()}:
                          </span>
                          <span>-{formatCurrency(value)}</span>
                        </div>
                      );
                    }
                    return null;
                  })}
                  <div className="flex justify-between text-sm font-semibold text-red-700 pt-1 border-t border-red-200">
                    <span>Total Deductions:</span>
                    <span>-{formatCurrency(appraisal.totalDeductions)}</span>
                  </div>
                </div>
              )}

              {/* Final Value */}
              <div className="flex justify-between items-center pt-3 border-t border-green-300">
                <span className="font-bold text-gray-900">Final Value:</span>
                <span className="text-2xl font-bold text-green-700">
                  {formatCurrency(appraisal.finalValue)}
                </span>
              </div>

              {/* Manual Adjustment Option */}
              <div className="mt-4 pt-4 border-t border-green-200">
                <label className="flex items-center gap-2 text-sm text-gray-700 mb-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showManualAdjustment}
                    onChange={(e) => {
                      setShowManualAdjustment(e.target.checked);
                      if (e.target.checked) {
                        setFormData((prev) => ({
                          ...prev,
                          agreedValue: appraisal.finalValue,
                        }));
                      }
                    }}
                    className="rounded"
                  />
                  <span>Manual adjustment needed</span>
                </label>

                {showManualAdjustment && (
                  <div className="space-y-3 mt-3">
                    <Input
                      label="Adjusted Value *"
                      type="number"
                      step="0.01"
                      value={formData.agreedValue}
                      onChange={(e) =>
                        handleChange("agreedValue", e.target.value)
                      }
                      placeholder={appraisal.finalValue.toString()}
                      required
                    />
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Adjustment Reason *
                      </label>
                      <textarea
                        value={formData.adjustmentReason || ""}
                        onChange={(e) =>
                          handleChange("adjustmentReason", e.target.value)
                        }
                        rows={2}
                        placeholder="Explain why value was adjusted (required for manual adjustments)"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                        required
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg text-center">
              <FiInfo className="w-5 h-5 mx-auto mb-2 text-gray-400" />
              <p className="text-sm text-gray-500">
                Enter net weight, purity, and item type to calculate value
              </p>
            </div>
          )}

          {/* Info Box */}
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
            <FiInfo className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-blue-800">
              Exchange items will be deducted from the final order amount. The
              system calculates value based on current market rates and item
              condition.
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowAddModal(false);
                setFormData(getInitialFormData());
                dispatch(clearExchangeCalculation());
                setShowManualAdjustment(false);
              }}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={handleSave}
              disabled={
                isCalculating ||
                (!appraisal && !showManualAdjustment) ||
                !selectedBranch
              }
            >
              <FiCheck className="mr-2" />
              {editingIndex !== null ? "Update Item" : "Add Item"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

// Exchange Item Card Component
const ExchangeItemCard = ({
  item,
  index,
  onEdit,
  onRemove,
  onAccept,
  onUnaccept,
  formatCurrency,
  getStatusBadge,
  getConditionColor,
  readOnly,
}) => {
  const statusBadge = getStatusBadge(item);
  const isAccepted = item.customerAccepted || item.status === "ACCEPTED";

  // Get the display value
  const displayValue =
    item.agreedValue || item.systemAppraisal?.finalValue || 0;
  const evaluatedValue =
    item.systemAppraisal?.grossValue || item.evaluatedValue || 0;
  const deductions =
    item.systemAppraisal?.totalDeductions || item.deductions || 0;

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden hover:border-gray-300 transition-colors">
      {/* Header */}
      <div className="p-4 bg-gray-50 border-b">
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="px-2 py-1 text-xs font-medium bg-white border border-gray-300 rounded">
                {item.itemType}
              </span>
              <Badge variant={statusBadge.variant} size="sm">
                {statusBadge.label}
              </Badge>
              <Badge variant={getConditionColor(item.conditionGrade)} size="sm">
                {item.conditionGrade}
              </Badge>
            </div>
            <h4 className="font-semibold text-gray-900">{item.description}</h4>
            {item.brand && (
              <p className="text-sm text-gray-500 mt-1">Brand: {item.brand}</p>
            )}
          </div>

          {!readOnly && (
            <div className="flex gap-1 ml-3">
              {isAccepted ? (
                <button
                  type="button"
                  onClick={onUnaccept}
                  className="p-1.5 hover:bg-orange-100 rounded text-orange-600 transition-colors"
                  title="Reset acceptance"
                >
                  <FiRotateCcw size={16} />
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={onEdit}
                    className="p-1.5 hover:bg-blue-100 rounded text-blue-600 transition-colors"
                    title="Edit item"
                  >
                    <FiEdit3 size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={onRemove}
                    className="p-1.5 hover:bg-red-100 rounded text-red-600 transition-colors"
                    title="Remove item"
                  >
                    <FiTrash2 size={16} />
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="p-4">
        {/* Weight & Metal Details */}
        <div className="grid grid-cols-3 gap-3 text-sm mb-4">
          <div>
            <p className="text-xs text-gray-500">Gross Weight</p>
            <p className="font-medium">{item.grossWeight}g</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Net Weight</p>
            <p className="font-medium">{item.netWeight}g</p>
          </div>
          <div>
            <p className="text-xs text-gray-500">Stone Weight</p>
            <p className="font-medium">{item.stoneWeight || 0}g</p>
          </div>
        </div>

        {/* Metal Details */}
        {item.metalDetails && (
          <div className="grid grid-cols-2 gap-3 text-sm mb-4 p-3 bg-yellow-50 rounded">
            <div>
              <p className="text-xs text-gray-500">Purity</p>
              <p className="font-medium">{item.metalDetails.purity || "-"}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Color</p>
              <p className="font-medium">{item.metalDetails.color || "-"}</p>
            </div>
          </div>
        )}

        {/* Condition Notes */}
        {item.conditionNotes && (
          <div className="mb-4 p-3 bg-gray-50 rounded text-sm">
            <p className="text-xs text-gray-500 mb-1">Condition Notes</p>
            <p className="text-gray-700">{item.conditionNotes}</p>
          </div>
        )}

        {/* Manual Adjustment Indicator */}
        {item.manualAdjustment && (
          <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded text-sm">
            <div className="flex items-start gap-2">
              <FiAlertCircle className="text-orange-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-orange-900 mb-1">
                  Manually Adjusted
                </p>
                <p className="text-orange-700 text-xs">
                  {item.manualAdjustment.adjustmentReason}
                </p>
                {item.manualAdjustment.adjustedBy && (
                  <p className="text-orange-600 text-xs mt-1">
                    By: {item.manualAdjustment.adjustedBy}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Valuation */}
        <div className="p-3 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg">
          <div className="flex justify-between items-center text-sm mb-2">
            <span className="text-gray-600">Evaluated Value:</span>
            <span className="font-semibold text-gray-900">
              {formatCurrency(evaluatedValue)}
            </span>
          </div>
          {deductions > 0 && (
            <div className="flex justify-between items-center text-sm mb-2 text-red-600">
              <span>Deductions:</span>
              <span className="font-semibold">
                -{formatCurrency(deductions)}
              </span>
            </div>
          )}
          <div className="flex justify-between items-center pt-2 border-t border-green-300">
            <span className="font-bold text-gray-900">
              {isAccepted ? "Agreed Value:" : "Current Value:"}
            </span>
            <span className="text-xl font-bold text-green-700">
              {formatCurrency(displayValue)}
            </span>
          </div>
        </div>

        {/* Accept/Reject Actions */}
        {!readOnly && !isAccepted && (
          <div className="mt-4 flex gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={onEdit}
              className="flex-1"
            >
              <FiEdit3 size={14} className="mr-1" />
              Edit
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={onAccept}
              className="flex-1 bg-green-600 hover:bg-green-700 text-white"
            >
              <FiCheck size={14} className="mr-1" />
              Accept
            </Button>
          </div>
        )}

        {isAccepted && (
          <div className="mt-4 p-2 bg-green-100 border border-green-300 rounded text-center">
            <div className="flex items-center justify-center gap-2 text-green-800 font-medium text-sm">
              <FiCheck />
              <span>Accepted & Applied to Order</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EnhancedExchangeSection;
